import { useEffect, useRef, useState } from 'react'
import { Link2, Network, Sparkles, Wand2, X } from 'lucide-react'
import { CARD_COLUMNS } from '../data'
import { cardColor, charCount, columnMeta, relativeTime, visibleSnippet } from '../lib/utils'

const GRAPH_LAYOUT = { inspiration: 0.17, draft: 0.39, polish: 0.61, done: 0.83 }

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

export default function GraphView({
  project,
  selectedCardId,
  onOpenCard,
  onOpenDocument,
  onMoveCardPosition,
  onConnectCards,
  onAutoArrange,
  notify,
}) {
  const canvasRef = useRef(null)
  const dragRef = useRef(null)
  const latestRef = useRef({})
  const suppressClickRef = useRef(false)
  const [dragPositions, setDragPositions] = useState({})
  const [draggingId, setDraggingId] = useState(null)
  const [linkMode, setLinkMode] = useState(false)
  const [linkSource, setLinkSource] = useState(null)

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setLinkMode(false)
        setLinkSource(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const positions = {}
  project.cards.forEach((card, index) => {
    const fallback = {
      x: 0.14 + (index % 4) * 0.22,
      y: 0.2 + Math.floor(index / 4) * 0.27,
    }
    positions[card.id] = dragPositions[card.id] || card.graph || fallback
  })

  function startDrag(event, card) {
    if (linkMode || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const pos = positions[card.id]
    dragRef.current = {
      id: card.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: pos.x,
      originY: pos.y,
      moved: false,
    }
    latestRef.current[card.id] = pos
    setDraggingId(card.id)
  }

  function moveCard(event) {
    const drag = dragRef.current
    if (!drag) return
    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY
    if (Math.abs(deltaX) + Math.abs(deltaY) > 3) drag.moved = true
    const rect = canvasRef.current.getBoundingClientRect()
    const next = {
      x: clamp(drag.originX + deltaX / rect.width, 0.08, 0.92),
      y: clamp(drag.originY + deltaY / rect.height, 0.1, 0.9),
    }
    latestRef.current[drag.id] = next
    setDragPositions((current) => ({ ...current, [drag.id]: next }))
  }

  function endDrag(event) {
    const drag = dragRef.current
    if (!drag) return
    const next = latestRef.current[drag.id]
    if (next) onMoveCardPosition(drag.id, next)
    if (drag.moved) {
      suppressClickRef.current = true
      window.setTimeout(() => { suppressClickRef.current = false }, 0)
      setDragPositions((current) => {
        const copy = { ...current }
        delete copy[drag.id]
        return copy
      })
    }
    dragRef.current = null
    setDraggingId(null)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  function handleNodeClick(card) {
    if (suppressClickRef.current) {
      suppressClickRef.current = false
      return
    }
    if (linkMode) {
      if (!linkSource) {
        setLinkSource(card.id)
        return
      }
      if (linkSource === card.id) {
        notify('一张卡片不能连向自己。')
        return
      }
      const already = project.connections.some((link) =>
        (link.source === linkSource && link.target === card.id) ||
        (link.source === card.id && link.target === linkSource)
      )
      if (already) {
        notify('这两张卡片已经有关联了。')
        return
      }
      onConnectCards(linkSource, card.id)
      setLinkMode(false)
      setLinkSource(null)
      return
    }
    onOpenCard(card.id)
  }

  function arrange() {
    const grouped = {}
    CARD_COLUMNS.forEach((column) => { grouped[column.id] = [] })
    project.cards.forEach((card) => { (grouped[card.column] || grouped.inspiration).push(card) })
    const updates = {}
    CARD_COLUMNS.forEach((column) => {
      const cards = grouped[column.id] || []
      cards.forEach((card, index) => {
        const spread = cards.length <= 1 ? 0.5 : 0.19 + (index / (cards.length - 1)) * 0.62
        updates[card.id] = { x: GRAPH_LAYOUT[column.id], y: spread }
      })
    })
    onAutoArrange(updates)
    notify('已按叙事阶段重新排布卡片。')
  }

  const activeSource = project.cards.find((card) => card.id === linkSource)

  return (
    <div className="graph-view">
      <header className="view-head">
        <div className="view-title">
          <div className="eyebrow-row">
            <span className="eyebrow-dot" />
            RELATION MAP
            <span className="eyebrow-slash">/</span>
            关系可视化
          </div>
          <h1>关联看板<span className="ink-dot">.</span></h1>
          <p className="view-subtitle">拖动卡片整理思路，把看不见的伏笔连成一张网。</p>
          <div className="view-meta-row">
            <span><strong>{project.cards.length}</strong> 张卡片</span>
            <i />
            <span><strong>{project.connections.length}</strong> 条关系</span>
            <i />
            <span className="muted-meta">最近整理 {relativeTime(project.updatedAt)}</span>
          </div>
        </div>
        <div className="view-actions">
          <button className="btn btn--outline" onClick={arrange}>
            <Wand2 size={16} /> 智能整理
          </button>
          <button
            className={'btn ' + (linkMode ? 'btn--accent' : 'btn--ink')}
            onClick={() => { setLinkMode(!linkMode); setLinkSource(null) }}
          >
            {linkMode ? <X size={16} /> : <Link2 size={16} />}
            {linkMode ? '退出连线' : '建立关联'}
          </button>
        </div>
      </header>

      <div className="graph-stage">
        <div className="graph-canvas" ref={canvasRef}>
          <div className="graph-grid" />
          <div className="graph-watermark">
            <Network size={18} />
            <span>拖动节点 · 建立关系</span>
          </div>

          <svg className="graph-edges" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {project.connections.map((link) => {
              const source = positions[link.source]
              const target = positions[link.target]
              if (!source || !target) return null
              const x1 = source.x * 100
              const y1 = source.y * 100
              const x2 = target.x * 100
              const y2 = target.y * 100
              const midX = (x1 + x2) / 2
              const isSelected = selectedCardId === link.source || selectedCardId === link.target
              return (
                <path
                  key={link.id}
                  d={'M ' + x1 + ' ' + y1 + ' C ' + midX + ' ' + y1 + ', ' + midX + ' ' + y2 + ', ' + x2 + ' ' + y2}
                  className={'graph-edge ' + (isSelected ? 'is-selected' : '')}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
              )
            })}
          </svg>

          {project.connections.map((link) => {
            const source = positions[link.source]
            const target = positions[link.target]
            if (!source || !target) return null
            return (
              <span
                key={'label-' + link.id}
                className="graph-edge-label"
                style={{ left: ((source.x + target.x) / 2) * 100 + '%', top: ((source.y + target.y) / 2) * 100 + '%' }}
              >
                {link.label || '相关'}
              </span>
            )
          })}

          {project.cards.map((card) => {
            const pos = positions[card.id] || { x: 0.3, y: 0.3 }
            const status = columnMeta(card.column)
            const links = project.connections.filter((link) => link.source === card.id || link.target === card.id).length
            const isSource = linkSource === card.id
            return (
              <div
                key={card.id}
                className={[
                  'graph-node',
                  draggingId === card.id ? 'is-dragging' : '',
                  selectedCardId === card.id ? 'is-open' : '',
                  isSource ? 'is-source' : '',
                  linkMode ? 'is-linkable' : '',
                ].join(' ')}
                style={{ left: pos.x * 100 + '%', top: pos.y * 100 + '%', '--card-tint': cardColor(card) }}
                onPointerDown={(event) => startDrag(event, card)}
                onPointerMove={moveCard}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onClick={() => handleNodeClick(card)}
                onDoubleClick={() => onOpenDocument(card.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleNodeClick(card)
                }}
                aria-label={card.title}
              >
                <div className="graph-node__top">
                  <span className="graph-node__status"><i style={{ background: status.color }} />{status.label}</span>
                  <span className="graph-node__links"><Link2 size={11} /> {links}</span>
                </div>
                <h3>{card.title}</h3>
                <p>{visibleSnippet(card.content, 58)}</p>
                <div className="graph-node__foot">
                  <div>{(card.tags || []).slice(0, 2).map((tag) => <span key={tag}>{tag}</span>)}</div>
                  <em>{charCount(card.content)} 字</em>
                </div>
                {isSource ? <span className="graph-node__source">连线起点</span> : null}
              </div>
            )
          })}

          {linkMode ? (
            <div className="link-banner">
              <span className="link-banner__icon"><Link2 size={15} /></span>
              {activeSource ? (
                <>
                  <p>已选《{activeSource.title}》<span>再点一张卡片完成连接</span></p>
                  <button onClick={() => setLinkSource(null)}>重选</button>
                </>
              ) : (
                <>
                  <p>选择一张卡片开始连线<span>点第一张卡片，再点另一张</span></p>
                  <button onClick={() => { setLinkMode(false); setLinkSource(null) }}>取消</button>
                </>
              )}
            </div>
          ) : null}

          <div className="graph-legend">
            {CARD_COLUMNS.map((column) => (
              <span key={column.id}><i style={{ background: column.color }} />{column.label}</span>
            ))}
            <em><Sparkles size={12} /> 关系会被保留到你看板上</em>
          </div>
        </div>
      </div>
    </div>
  )
}
